import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../api/client.js';
import clsx from 'clsx';

export default function StaleFollowups() {
  const [minDays, setMinDays] = useState(7);
  const [selected, setSelected] = useState(new Set());
  const [actionState, setActionState] = useState(null); // 'reassign' | 'escalate' | 'nudge' | null
  const [actionError, setActionError] = useState(null);
  const qc = useQueryClient();

  const data = useQuery({
    queryKey: ['admin-stale-followups', minDays],
    queryFn: () => api.get(`/admin/stale-followups?min_days=${minDays}`).then(r => r.data.data),
  });

  // Eligible reassignment targets — sales-role users in caller's tenant
  const users = useQuery({
    queryKey: ['admin-users-roster'],
    queryFn: () => api.get('/admin/users').then(r => r.data.data),
  });
  const reps = useMemo(
    () => (users.data || []).filter(u => u.role === 'sales' && u.status === 'active'),
    [users.data]
  );

  const items = data.data?.items || [];
  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const action = useMutation({
    mutationFn: async (body) => {
      const r = await api.patch('/admin/followups', body);
      return r.data.data;
    },
    onSuccess: () => {
      setSelected(new Set());
      setActionState(null);
      setActionError(null);
      qc.invalidateQueries({ queryKey: ['admin-stale-followups'] });
      qc.invalidateQueries({ queryKey: ['followups'] });
    },
    onError: (err) => {
      setActionError(err?.response?.data?.error || 'Action failed');
    },
  });

  function toggle(id) {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function selectAllVisible() {
    setSelected(new Set(items.map(f => f.id)));
  }
  function clearSelection() {
    setSelected(new Set());
  }

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Manager</div>
        <h1 className="text-2xl">Stale follow-ups</h1>
        <div className="rule-crimson" />
        <div className="text-sm text-muted mt-1">Pending items overdue by more than N days, ranked by urgency × customer value.</div>
      </header>

      <div className="flex items-center gap-2 text-sm flex-wrap">
        <span className="text-muted">Overdue more than:</span>
        {[3, 7, 14, 30].map(d => (
          <button key={d} onClick={() => setMinDays(d)}
            className={clsx('px-2 py-1 rounded',
              minDays === d ? 'bg-navy text-white' : 'bg-panel text-navy')}>
            {d} days
          </button>
        ))}
      </div>

      {data.isLoading && <div className="text-muted">Loading…</div>}

      {data.data && (
        <>
          {data.data.by_rep.length > 0 && (
            <section className="card p-4">
              <div className="section-eyebrow">By rep</div>
              <div className="rule-crimson" />
              <table className="w-full text-sm mt-2">
                <thead className="text-left text-xs text-muted">
                  <tr>
                    <th className="py-1">Rep</th>
                    <th className="text-right">High</th>
                    <th className="text-right">Medium</th>
                    <th className="text-right">Low</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.by_rep.map(r => (
                    <tr key={r.user_id} className="border-t border-panelDark">
                      <td className="py-1">
                        {r.user_id
                          ? <Link to={`/admin/reps/${r.user_id}`} className="text-navy hover:text-crimson">{r.full_name}</Link>
                          : <span className="italic text-muted">(unassigned)</span>}
                      </td>
                      <td className="text-right text-crimson font-semibold">{r.high}</td>
                      <td className="text-right text-gold">{r.medium}</td>
                      <td className="text-right text-muted">{r.low}</td>
                      <td className="text-right font-semibold">{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Action bar — appears once anything is selected */}
          {selected.size > 0 && (
            <section className="card p-3 sticky top-2 z-10 bg-panel ring-1 ring-navy">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-navy">
                  {selected.size} selected
                </span>
                <button className="text-xs text-crimson hover:underline ml-auto" onClick={clearSelection}>
                  clear
                </button>

                <ActionButton
                  label="Reassign"
                  active={actionState === 'reassign'}
                  onClick={() => { setActionState(actionState === 'reassign' ? null : 'reassign'); setActionError(null); }}
                />
                <ActionButton
                  label="Escalate"
                  active={actionState === 'escalate'}
                  onClick={() => { setActionState(actionState === 'escalate' ? null : 'escalate'); setActionError(null); }}
                />
                <ActionButton
                  label="Nudge"
                  active={actionState === 'nudge'}
                  onClick={() => { setActionState(actionState === 'nudge' ? null : 'nudge'); setActionError(null); }}
                />
                <button
                  className="px-3 py-1 text-sm rounded text-crimson hover:bg-rose-100"
                  onClick={() => {
                    if (window.confirm(`Delete ${selected.size} follow-up(s)? This cannot be undone.`)) {
                      action.mutate({ ids: selectedIds, action: 'delete' });
                    }
                  }}
                  disabled={action.isPending}>
                  Delete
                </button>
              </div>

              {actionState === 'reassign' && (
                <ReassignPanel
                  reps={reps}
                  count={selected.size}
                  pending={action.isPending}
                  onSubmit={(user_id) => action.mutate({ ids: selectedIds, action: 'reassign', user_id })}
                />
              )}
              {actionState === 'escalate' && (
                <EscalatePanel
                  count={selected.size}
                  pending={action.isPending}
                  onSubmit={(urgency) => action.mutate({ ids: selectedIds, action: 'escalate', urgency })}
                />
              )}
              {actionState === 'nudge' && (
                <NudgePanel
                  count={selected.size}
                  pending={action.isPending}
                  onSubmit={(message) => action.mutate({ ids: selectedIds, action: 'nudge', message })}
                />
              )}
              {actionError && (
                <div className="text-crimson text-sm mt-2">{actionError}</div>
              )}
            </section>
          )}

          <section className="card p-4">
            <div className="flex items-baseline justify-between gap-2">
              <div className="section-eyebrow">Items</div>
              {items.length > 0 && (
                <button onClick={selected.size === items.length ? clearSelection : selectAllVisible}
                  className="text-xs text-crimson hover:underline">
                  {selected.size === items.length ? 'Clear all' : `Select all ${items.length}`}
                </button>
              )}
            </div>
            <div className="rule-crimson" />
            {items.length === 0 && (
              <div className="text-muted text-sm mt-2">Nothing overdue. Nice.</div>
            )}
            <div className="space-y-2 mt-2">
              {items.map(f => (
                <div key={f.id} className={clsx('rounded p-3 border-l-4 bg-panel/40 flex gap-2',
                  f.urgency === 'high' ? 'border-crimson' : f.urgency === 'medium' ? 'border-gold' : 'border-navy-200',
                  selected.has(f.id) && 'ring-1 ring-navy bg-panel')}>
                  <input
                    type="checkbox"
                    className="mt-1 cursor-pointer"
                    checked={selected.has(f.id)}
                    onChange={() => toggle(f.id)}
                    aria-label={`Select ${f.title}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-medium text-navy">{f.title}</div>
                      <div className="text-xs text-right">
                        {f.customer_value > 0 && (
                          <div className="text-navy font-semibold">${Math.round(f.customer_value).toLocaleString()} / 12mo</div>
                        )}
                        <div className="text-crimson font-semibold">{f.days_overdue}d overdue</div>
                      </div>
                    </div>
                    <div className="text-xs text-muted mt-1">
                      {f.customer_name
                        ? <Link to={`/customers/${f.customer_id}`} className="text-navy hover:underline">{f.customer_name}</Link>
                        : <span className="italic">General task</span>}
                      {' · '}
                      {f.user_id
                        ? <Link to={`/admin/reps/${f.user_id}`} className="text-navy hover:underline">{f.user_name}</Link>
                        : <span className="italic">(unassigned)</span>}
                      {' · '}
                      <span className="capitalize">{f.urgency}</span>
                      {' · '}
                      <span>due {new Date(f.due_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function ActionButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={clsx('px-3 py-1 text-sm rounded',
        active ? 'bg-navy text-white' : 'bg-white text-navy hover:bg-panel')}>
      {label}
    </button>
  );
}

function ReassignPanel({ reps, count, pending, onSubmit }) {
  const [userId, setUserId] = useState('');
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <span className="text-sm">Reassign {count} item(s) to:</span>
      <select className="input py-1 text-sm" value={userId} onChange={e => setUserId(e.target.value)}>
        <option value="">— Select rep —</option>
        {reps.map(u => (
          <option key={u.id} value={u.id}>{u.full_name}{u.branch_name ? ` · ${u.branch_name}` : ''}</option>
        ))}
      </select>
      <button
        className="btn-primary py-1 text-sm"
        disabled={!userId || pending}
        onClick={() => onSubmit(parseInt(userId, 10))}>
        {pending ? 'Reassigning…' : 'Reassign'}
      </button>
    </div>
  );
}

function EscalatePanel({ count, pending, onSubmit }) {
  const [urgency, setUrgency] = useState('high');
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <span className="text-sm">Set urgency for {count} item(s) to:</span>
      <select className="input py-1 text-sm" value={urgency} onChange={e => setUrgency(e.target.value)}>
        <option value="high">High</option>
        <option value="medium">Medium</option>
        <option value="low">Low</option>
      </select>
      <button
        className="btn-primary py-1 text-sm"
        disabled={pending}
        onClick={() => onSubmit(urgency)}>
        {pending ? 'Updating…' : 'Apply'}
      </button>
    </div>
  );
}

function NudgePanel({ count, pending, onSubmit }) {
  const [message, setMessage] = useState('');
  return (
    <div className="mt-3 space-y-2">
      <div className="text-sm">Send a nudge to the assigned rep(s) for {count} item(s):</div>
      <input
        className="input text-sm"
        placeholder="Optional message — e.g. 'Customer called this morning, please follow up today.'"
        value={message}
        maxLength={500}
        onChange={e => setMessage(e.target.value)}
      />
      <div className="flex justify-end">
        <button
          className="btn-primary py-1 text-sm"
          disabled={pending}
          onClick={() => onSubmit(message)}>
          {pending ? 'Sending…' : 'Send nudge'}
        </button>
      </div>
    </div>
  );
}
